/**
 * src/three/GunViewmodel.js
 * มินิกัน GAU-2B/A มุมมอง FPS — 6 ลำกล้องหมุน (รูปลักษณ์อย่างเดียว)
 */
import * as THREE from 'three';
import { T3 } from './config.js';

export default class GunViewmodel {
  constructor(camera) {
    this.camera = camera;
    this.group = new THREE.Group();
    camera.add(this.group);

    this.recoil = 0;
    this.bob = 0;
    this.flashTimer = 0;
    this.smokePuffs = [];
    this.spin = 0;
    this.spinVel = 0;

    const metal = new THREE.MeshStandardMaterial({
      color: 0x4a4e52, roughness: 0.42, metalness: 0.78,
    });
    const metalHi = new THREE.MeshStandardMaterial({
      color: 0x6a7076, roughness: 0.3, metalness: 0.85,
    });
    const metalDark = new THREE.MeshStandardMaterial({
      color: 0x2a2c30, roughness: 0.48, metalness: 0.72,
    });
    const polymer = new THREE.MeshStandardMaterial({
      color: 0x1a1816, roughness: 0.9, metalness: 0.08,
    });
    const gripMat = new THREE.MeshStandardMaterial({
      color: 0x2c241c, roughness: 0.95, metalness: 0.04,
    });
    const glove = new THREE.MeshStandardMaterial({
      color: 0x3a322c, roughness: 0.85, metalness: 0.05,
    });
    const gloveDark = new THREE.MeshStandardMaterial({
      color: 0x2a241e, roughness: 0.9, metalness: 0.04,
    });
    const sleeve = new THREE.MeshStandardMaterial({
      color: 0x1c1814, roughness: 0.95, metalness: 0.02,
    });

    const gun = new THREE.Group();
    this.group.add(gun);

    // —— ชุดลำกล้อง 6 ท่อหมุน (GAU-2B/A) ——
    this.barrelCluster = new THREE.Group();
    this.barrelCluster.position.set(0, 0.1, -0.15);
    gun.add(this.barrelCluster);

    const barrelLen = 0.95;
    const barrelR = 0.018;
    const clusterR = 0.048;
    const barrelGeo = new THREE.CylinderGeometry(barrelR, barrelR * 1.05, barrelLen, 10);
    barrelGeo.rotateX(Math.PI / 2);

    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const b = new THREE.Mesh(barrelGeo, metalDark);
      b.position.set(Math.cos(a) * clusterR, Math.sin(a) * clusterR, -barrelLen * 0.5);
      this.barrelCluster.add(b);

      // ปลายปากกระบอกหนาขึ้นนิด
      const tip = new THREE.Mesh(
        new THREE.CylinderGeometry(barrelR * 1.15, barrelR * 1.05, 0.04, 8),
        metalHi
      );
      tip.rotation.x = Math.PI / 2;
      tip.position.set(Math.cos(a) * clusterR, Math.sin(a) * clusterR, -barrelLen);
      this.barrelCluster.add(tip);
    }

    // จานยึดลำกล้อง 3 วง (ตามรูป)
    const clampZs = [-0.12, -0.48, -0.88];
    for (const z of clampZs) {
      const clamp = new THREE.Mesh(
        new THREE.CylinderGeometry(0.078, 0.078, 0.038, 20),
        metal
      );
      clamp.rotation.x = Math.PI / 2;
      clamp.position.set(0, 0, z);
      this.barrelCluster.add(clamp);

      // รูกลางจาน
      const rim = new THREE.Mesh(
        new THREE.TorusGeometry(0.078, 0.006, 6, 20),
        metalDark
      );
      rim.position.set(0, 0, z);
      this.barrelCluster.add(rim);
    }

    // จานหน้าสุด (muzzle plate)
    const frontPlate = new THREE.Mesh(
      new THREE.CylinderGeometry(0.082, 0.08, 0.03, 20),
      metalHi
    );
    frontPlate.rotation.x = Math.PI / 2;
    frontPlate.position.set(0, 0, -0.98);
    this.barrelCluster.add(frontPlate);

    // แกนกลาง
    const axle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.016, 0.016, 0.92, 8),
      metalHi
    );
    axle.rotation.x = Math.PI / 2;
    axle.position.set(0, 0, -0.48);
    this.barrelCluster.add(axle);

    // จุดปากกระบอก (กลางคลัสเตอร์) สำหรับแฟลช/แสง
    this.muzzleLocal = new THREE.Vector3(0, 0.1, -1.18);

    // —— ตัวเรือนหลัง / มอเตอร์ ——
    const housing = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.135, 0.42, 16),
      metalDark
    );
    housing.rotation.x = Math.PI / 2;
    housing.position.set(0, 0.1, 0.22);
    gun.add(housing);

    const housingFront = new THREE.Mesh(
      new THREE.CylinderGeometry(0.11, 0.12, 0.08, 16),
      metal
    );
    housingFront.rotation.x = Math.PI / 2;
    housingFront.position.set(0, 0.1, 0.0);
    gun.add(housingFront);

    // ฝาครอบด้านบน (มอเตอร์ / สาย)
    const topCover = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.1, 0.32),
      metal
    );
    topCover.position.set(0, 0.22, 0.2);
    gun.add(topCover);

    // สลัก/น็อตตามเรือน
    for (let i = 0; i < 4; i++) {
      const bolt = new THREE.Mesh(
        new THREE.CylinderGeometry(0.012, 0.012, 0.02, 8),
        metalHi
      );
      bolt.rotation.x = Math.PI / 2;
      const a = (i / 4) * Math.PI * 2 + 0.4;
      bolt.position.set(Math.cos(a) * 0.11, 0.1 + Math.sin(a) * 0.11, 0.35);
      gun.add(bolt);
    }

    // แท่นยึดด้านข้าง
    for (const side of [-1, 1]) {
      const mount = new THREE.Mesh(
        new THREE.BoxGeometry(0.04, 0.08, 0.22),
        metalDark
      );
      mount.position.set(side * 0.14, 0.08, 0.18);
      gun.add(mount);
    }

    // กล่องป้อนกระสุนด้านซ้าย
    const feedBox = new THREE.Mesh(
      new THREE.BoxGeometry(0.14, 0.16, 0.2),
      metalDark
    );
    feedBox.position.set(-0.16, 0.02, 0.22);
    gun.add(feedBox);

    const feedLid = new THREE.Mesh(
      new THREE.BoxGeometry(0.145, 0.02, 0.205),
      metal
    );
    feedLid.position.set(-0.16, 0.11, 0.22);
    gun.add(feedLid);

    // สายเคเบิลบนเรือน
    const cable = new THREE.Mesh(
      new THREE.TorusGeometry(0.06, 0.008, 6, 12, Math.PI * 1.2),
      metalHi
    );
    cable.rotation.y = Math.PI / 2;
    cable.position.set(0.08, 0.2, 0.32);
    gun.add(cable);

    // ท้ายเรือน / แผ่นหลัง
    const rearPlate = new THREE.Mesh(
      new THREE.CylinderGeometry(0.13, 0.12, 0.06, 16),
      metal
    );
    rearPlate.rotation.x = Math.PI / 2;
    rearPlate.position.set(0, 0.1, 0.45);
    gun.add(rearPlate);

    // —— กริปสองมือแบบมินิกัน ——
    const gripBack = new THREE.Mesh(
      new THREE.BoxGeometry(0.07, 0.2, 0.09),
      gripMat
    );
    gripBack.position.set(0, -0.12, 0.38);
    gripBack.rotation.x = 0.35;
    gun.add(gripBack);

    const spadeL = new THREE.Mesh(
      new THREE.BoxGeometry(0.045, 0.12, 0.05),
      polymer
    );
    spadeL.position.set(-0.08, -0.02, 0.48);
    spadeL.rotation.z = 0.4;
    gun.add(spadeL);

    const spadeR = new THREE.Mesh(
      new THREE.BoxGeometry(0.045, 0.12, 0.05),
      polymer
    );
    spadeR.position.set(0.08, -0.02, 0.48);
    spadeR.rotation.z = -0.4;
    gun.add(spadeR);

    const triggerBar = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.025, 0.04),
      metalHi
    );
    triggerBar.position.set(0, -0.05, 0.42);
    gun.add(triggerBar);

    // —— มือ ——
    const handR = new THREE.Group();
    handR.position.set(0.12, -0.08, 0.46);
    handR.rotation.set(0.35, 0.2, -0.4);

    const sleeveR = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.08, 0.16), sleeve);
    sleeveR.position.set(0.04, 0.02, 0.14);
    handR.add(sleeveR);
    const wristR = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.065, 0.06), gloveDark);
    wristR.position.set(0.02, 0.0, 0.05);
    handR.add(wristR);
    const palmR = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.05, 0.1), glove);
    palmR.position.set(0.01, -0.01, -0.02);
    handR.add(palmR);
    for (const f of [
      { x: 0.03, len: 0.09, rx: 1.1 },
      { x: 0.0, len: 0.1, rx: 1.2 },
      { x: -0.03, len: 0.09, rx: 1.25 },
    ]) {
      const dig = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.028, f.len), glove);
      dig.position.set(f.x, -0.025, -0.02);
      dig.rotation.x = f.rx;
      handR.add(dig);
    }
    gun.add(handR);

    const handL = new THREE.Group();
    handL.position.set(-0.12, -0.06, 0.44);
    handL.rotation.set(0.3, -0.15, 0.45);

    const sleeveL = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.08, 0.15), sleeve);
    sleeveL.position.set(-0.02, 0.02, 0.13);
    handL.add(sleeveL);
    const wristL = new THREE.Mesh(new THREE.BoxGeometry(0.075, 0.06, 0.055), gloveDark);
    wristL.position.set(0, 0.0, 0.04);
    handL.add(wristL);
    const palmL = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.05, 0.1), glove);
    palmL.position.set(0, -0.01, -0.02);
    handL.add(palmL);
    for (const f of [
      { x: 0.03, len: 0.09, rx: 1.05 },
      { x: 0.0, len: 0.1, rx: 1.15 },
      { x: -0.03, len: 0.09, rx: 1.2 },
    ]) {
      const dig = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.028, f.len), glove);
      dig.position.set(f.x, -0.025, -0.02);
      dig.rotation.x = f.rx;
      handL.add(dig);
    }
    gun.add(handL);

    // จัดมุมมอง FPS — มินิกันใหญ่ขึ้นนิด
    this.restPos = new THREE.Vector3(0.26, -0.28, -0.38);
    this.group.position.copy(this.restPos);
    this.group.rotation.set(0, 0, 0);
    gun.scale.set(0.95, 0.95, 0.95);

    this._fwd = new THREE.Vector3(0, 0, -1);
    this._aimCam = new THREE.Vector3();
    this._muzzleCam = new THREE.Vector3();
    this._toAim = new THREE.Vector3();
    this._aimQuat = new THREE.Quaternion();
    this.aimDistance = 28;

    // แสงปากกระบอก
    this.muzzleLight = new THREE.PointLight(0xffe0a0, 0, 6, 2);
    this.muzzleLight.position.copy(this.muzzleLocal);
    this.group.add(this.muzzleLight);

    const flashCanvas = document.createElement('canvas');
    flashCanvas.width = 64;
    flashCanvas.height = 64;
    const fctx = flashCanvas.getContext('2d');
    const grd = fctx.createRadialGradient(32, 32, 2, 32, 32, 30);
    grd.addColorStop(0, 'rgba(255,255,240,1)');
    grd.addColorStop(0.25, 'rgba(255,200,120,0.9)');
    grd.addColorStop(0.55, 'rgba(255,120,40,0.35)');
    grd.addColorStop(1, 'rgba(0,0,0,0)');
    fctx.fillStyle = grd;
    fctx.fillRect(0, 0, 64, 64);
    const flashTex = new THREE.CanvasTexture(flashCanvas);
    this.flash = new THREE.Sprite(new THREE.SpriteMaterial({
      map: flashTex,
      transparent: true,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      opacity: 0,
    }));
    this.flash.position.copy(this.muzzleLocal);
    this.flash.scale.set(0.55, 0.55, 1);
    this.group.add(this.flash);

    this.spot = new THREE.SpotLight(0xfff0d0, 2.2, 22, 0.28, 0.45, 1.2);
    this.spot.position.copy(this.muzzleLocal);
    this.spot.target.position.set(0, 0.05, -8);
    this.group.add(this.spot);
    this.group.add(this.spot.target);
  }

  fire() {
    this.spinVel = Math.min(28, this.spinVel + 8);
    this.flashTimer = 0.05;
    this.muzzleLight.intensity = 16;
    this.flash.material.opacity = 1;
    // แฟลชเลื่อนตามลำกล้องที่หมุน
    const a = this.spin;
    this.flash.position.set(
      Math.cos(a) * 0.05,
      0.1 + Math.sin(a) * 0.05,
      -1.18
    );
    this.flash.scale.set(0.38 + Math.random() * 0.28, 0.38 + Math.random() * 0.28, 1);
    this.spawnSmoke();
  }

  spawnSmoke() {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    const g = ctx.createRadialGradient(32, 32, 4, 32, 32, 30);
    g.addColorStop(0, 'rgba(220,210,200,0.55)');
    g.addColorStop(0.5, 'rgba(160,150,140,0.25)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    const tex = new THREE.CanvasTexture(canvas);
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
      opacity: 0.7,
    }));
    spr.position.copy(this.muzzleLocal);
    spr.position.x += (Math.random() - 0.5) * 0.06;
    spr.position.y += (Math.random() - 0.5) * 0.06;
    spr.scale.set(0.14, 0.14, 1);
    this.group.add(spr);
    this.smokePuffs.push({
      mesh: spr,
      t: 0.45,
      max: 0.45,
      vel: new THREE.Vector3(
        (Math.random() - 0.5) * 0.18,
        0.12 + Math.random() * 0.12,
        -0.28 - Math.random() * 0.22
      ),
    });
  }

  update(dt) {
    this.bob += dt;
    this.recoil = Math.max(0, this.recoil - dt * 9);
    this.flashTimer = Math.max(0, this.flashTimer - dt);
    if (this.flashTimer <= 0) {
      this.muzzleLight.intensity = 0;
      this.flash.material.opacity = Math.max(0, this.flash.material.opacity - dt * 18);
    }

    // ลำกล้องหมุนตอนยิง แล้วค่อยชะลอ
    this.spin += this.spinVel * dt;
    this.spinVel = Math.max(0, this.spinVel - dt * 10);
    if (this.barrelCluster) {
      this.barrelCluster.rotation.z = this.spin;
    }

    const breath = Math.sin(this.bob * 1.5) * 0.005;
    this.group.position.set(
      this.restPos.x,
      this.restPos.y + breath,
      this.restPos.z
    );

    this.group.quaternion.identity();
    this.group.updateMatrix();
    this._muzzleCam.copy(this.muzzleLocal).applyMatrix4(this.group.matrix);
    const aimY = T3.AIM_NDC_Y ?? -0.08;
    const halfH = Math.tan((this.camera.fov * Math.PI) / 360) * this.aimDistance;
    this._aimCam.set(0, aimY * halfH, -this.aimDistance);
    this._toAim.subVectors(this._aimCam, this._muzzleCam).normalize();
    this._aimQuat.setFromUnitVectors(this._fwd, this._toAim);
    this.group.quaternion.copy(this._aimQuat);

    for (let i = this.smokePuffs.length - 1; i >= 0; i--) {
      const s = this.smokePuffs[i];
      s.t -= dt;
      s.mesh.position.addScaledVector(s.vel, dt);
      const k = s.t / s.max;
      s.mesh.material.opacity = 0.65 * k;
      const sc = 0.14 + (1 - k) * 0.5;
      s.mesh.scale.set(sc, sc, 1);
      if (s.t <= 0) {
        this.group.remove(s.mesh);
        s.mesh.material.map?.dispose();
        s.mesh.material.dispose();
        this.smokePuffs.splice(i, 1);
      }
    }
  }

  getMuzzleWorld(target = new THREE.Vector3()) {
    return this.group.localToWorld(target.copy(this.muzzleLocal));
  }

  dispose() {
    this.camera.remove(this.group);
  }
}
